// The orb's REVISE row (CLIO): four round outcome buttons —
//   ✓ green Complete · ◐ yellow Partial · ✕ red Incomplete · ↻ orange Reschedule.
// Compact (a thin or even band): the four buttons only. Open (the row is big):
// the buttons, an optional notes box ("What happened, and what needs to
// change?") and → to the CLIO tool.
import React, { useEffect, useState } from 'react';
import { Pressable, TextInput, View } from 'react-native';
import { ArrowRight, CalendarClock, Check, X } from 'lucide-react-native';
import { useV10 } from '../theme';
import { useK } from './ui';

export type ReviseOutcomeId = 'complete' | 'partial' | 'incomplete' | 'reschedule';
export const REVISE_NOTE_Q = 'What happened, and what needs to change?';

const HalfMoon = ({ size, color }: { size: number; color: string }) => (
  <View style={{ width: size, height: size, borderRadius: size / 2, borderWidth: 2, borderColor: color, overflow: 'hidden', flexDirection: 'row' }}>
    <View style={{ flex: 1, backgroundColor: color }} />
    <View style={{ flex: 1 }} />
  </View>
);

export const REVISE_OUTCOMES: { id: ReviseOutcomeId; label: string; color: string }[] = [
  { id: 'complete', label: 'Complete', color: '#3fbf6a' },
  { id: 'partial', label: 'Partially complete', color: '#f2c94c' },
  { id: 'incomplete', label: 'Incomplete', color: '#e5484d' },
  { id: 'reschedule', label: 'Reschedule', color: '#f2994a' },
];

export function ReviseOutcome({
  value,
  onPick,
  compact,
  size,
  note,
  onNote,
  onOpen,
}: {
  value: ReviseOutcomeId | null;
  onPick: (v: ReviseOutcomeId | null) => void;
  compact: boolean;
  /** Button diameter in px (before scaling); defaults to 40. */
  size?: number;
  note?: string;
  onNote?: (v: string) => void;
  onOpen?: () => void;
}) {
  const t = useV10();
  const k = useK();
  // Open: fit four buttons and three gaps into the measured width.
  const [boxW, setBoxW] = useState(0);
  const want = (size ?? 40) * (compact ? 1 : k);
  const D = Math.round(!compact && boxW ? Math.min(want, boxW / 5.6) : want);
  const [draft, setDraft] = useState(note ?? '');
  useEffect(() => setDraft(note ?? ''), [note]);

  const buttons = (
    <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: Math.round(D * 0.45) }}>
      {REVISE_OUTCOMES.map((o) => {
        const on = value === o.id;
        const fg = on ? '#1b1405' : o.color;
        const ic = Math.round(D * 0.52);
        return (
          <Pressable
            key={o.id}
            onPress={() => onPick(on ? null : o.id)}
            accessibilityRole="button"
            accessibilityLabel={o.label}
            accessibilityState={{ selected: on }}
            style={[
              { width: D, height: D, borderRadius: D / 2, borderWidth: 2, borderColor: o.color, backgroundColor: on ? o.color : 'transparent', alignItems: 'center', justifyContent: 'center' },
              { outlineStyle: 'none' } as object,
            ]}
          >
            {o.id === 'complete' ? (
              <Check size={ic} color={fg} strokeWidth={3} />
            ) : o.id === 'incomplete' ? (
              <X size={ic} color={fg} strokeWidth={3} />
            ) : o.id === 'reschedule' ? (
              <CalendarClock size={ic} color={fg} strokeWidth={2.4} />
            ) : (
              <HalfMoon size={Math.round(D * 0.46)} color={fg} />
            )}
          </Pressable>
        );
      })}
    </View>
  );
  if (compact) return buttons;

  return (
    <View style={{ alignSelf: 'stretch', gap: 10 * k }} onLayout={(e) => setBoxW(e.nativeEvent.layout.width)}>
      {buttons}
      <View style={{ flexDirection: 'row', alignItems: 'stretch', gap: 8 * k }}>
        <TextInput
          value={draft}
          onChangeText={setDraft}
          onBlur={() => draft !== (note ?? '') && onNote?.(draft)}
          placeholder={REVISE_NOTE_Q}
          placeholderTextColor={t.ivoryFaint}
          multiline
          maxLength={600}
          style={[
            {
              flex: 1,
              minHeight: 52 * k,
              paddingHorizontal: 10 * k,
              paddingVertical: 8 * k,
              borderRadius: 10 * k,
              borderWidth: 1,
              borderColor: 'rgba(214,180,106,0.35)',
              color: t.ivory,
              fontSize: 13 * k,
              lineHeight: 18 * k,
              textAlignVertical: 'top',
            },
            { outlineStyle: 'none' } as object,
          ]}
        />
        {onOpen ? (
          <Pressable
            onPress={onOpen}
            accessibilityRole="button"
            accessibilityLabel="Open CLIO"
            style={[{ width: 40 * k, borderRadius: 10 * k, borderWidth: 1, borderColor: 'rgba(214,180,106,0.35)', alignItems: 'center', justifyContent: 'center' }, { outlineStyle: 'none' } as object]}
          >
            <ArrowRight size={20 * k} color={t.gold} />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}
